/* Invoice History's selection and figures (PSR #68, Duncan 2026-10-15), shared by the page
   (AR / Collections → Invoice History), its PDF report (pages/InvoiceReportPage.js) and its Excel
   workbook (lib/invoiceXlsx.js), so an export always shows what the page showed.
   Input is GET /api/invoice-history (server/routes/invoiceHistory.js has the definitions). */
import { formatMoney, formatMoneyWhole } from './format';
import { customerOfJob } from './statement';

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* Dates arrive as 'YYYY-MM-DD'. Read them as calendar days: new Date() would make them UTC
   midnight, the evening before in Central. */
export const partsOf = (ymd) => (ymd ? ymd.split('-').map(Number) : null);
export const fmtDay = (ymd, withYear = true) => {
  const p = partsOf(ymd);
  return p ? `${MONTHS[p[1] - 1]} ${p[2]}${withYear ? `, ${p[0]}` : ''}` : '–';
};
export const todayCentral = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });

/* Period keys: '2026-10' (month), '2026-Q3' (quarter), '2026' (year), 'all'. */
export const periodKey = (ymd, type) => {
  if (type === 'all') return 'all';
  if (!ymd) return null;
  const p = partsOf(ymd);
  if (type === 'month') return ymd.slice(0, 7);
  if (type === 'quarter') return `${p[0]}-Q${Math.ceil(p[1] / 3)}`;
  return String(p[0]);
};
export const stepPeriod = (key, type, dir) => {
  if (type === 'month') {
    const [y, m] = key.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 + dir, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  }
  if (type === 'quarter') {
    const [y, q] = key.split('-Q').map(Number);
    const n = y * 4 + (q - 1) + dir;
    return `${Math.floor(n / 4)}-Q${(n % 4) + 1}`;
  }
  if (type === 'year') return String(Number(key) + dir);
  return key;
};
export const periodLabel = (key, type) => {
  if (type === 'month') return `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}`;
  if (type === 'quarter') return `${key.slice(5)} ${key.slice(0, 4)}`;
  if (type === 'year') return key;
  return 'Whole project';
};

/* A credit memo has no invoice date of its own: it counts on the day it was applied. */
export const dateOf = (inv) => inv.date || inv.paid_on;

/* Reversals (Duncan's call with accounting, 2026-10-19): accounting never undoes an invoice, it
   posts a credit memo against it -- numbered after it, CM or CR before or after its number
   (CM142696, CR142592, 142652CR, 142015CM; CR in front missed TWU-L-01's re-issued invoice, Edward
   2026-10-30), for the same amount, in the next open month when the invoice's month is closed
   (Limestone, Oakwood and Penelope's August grant invoices were credited in September). The
   'JOB|number' keys of every credit memo and of each invoice one cancels in full, in whatever month
   each falls. */
export function reversalKeys(jobs) {
  const out = new Set();
  (jobs || []).forEach((j) => {
    const job = String(j.job).toUpperCase();
    const byNumber = new Map(j.invoices.map((i) => [String(i.number).toUpperCase(), i]));
    j.invoices.forEach((i) => {
      if (!(i.amount < -0.004)) return;
      out.add(`${job}|${i.number}`);
      const m = /^(?:CM|CR)(.+)$/i.exec(String(i.number)) || /^(.+?)(?:CR|CM)$/i.exec(String(i.number));
      const target = m && byNumber.get(m[1].toUpperCase());
      if (target && target !== i && Math.abs(target.amount + i.amount) < 0.005) out.add(`${job}|${target.number}`);
    });
  });
  return out;
}

/* Days to pay (Duncan 2026-10-15: "avg days to pay per customer"): invoice date to final payment,
   for invoices paid in full. One paid before its own date (a prepayment) is left out. */
export const SLOW_DAYS = 60; // a job averaging more than this shows in red
export const daysToPay = (inv) => {
  if (inv.status !== 'paid' || !inv.date || !inv.paid_on || inv.amount <= 0) return null;
  const [a, b] = [partsOf(inv.date), partsOf(inv.paid_on)];
  const n = Math.round((Date.UTC(b[0], b[1] - 1, b[2]) - Date.UTC(a[0], a[1] - 1, a[2])) / 86400000);
  return n >= 0 ? n : null;
};
export const payStatsOf = (days) => {
  if (days.length === 0) return null;
  const s = [...days].sort((x, y) => x - y);
  const mid = Math.floor(s.length / 2);
  return {
    avg: Math.round(s.reduce((sum, n) => sum + n, 0) / s.length),
    median: s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2),
    n: s.length,
  };
};
export const dayWord = (n) => `${n} day${n === 1 ? '' : 's'}`;

/* Money with the minus outside the dollar sign: −$70.00. */
export const money = (n) => (n < -0.004 ? `−${formatMoney(-n)}` : formatMoney(n));
export const moneyWhole = (n) => (n < -0.5 ? `−${formatMoneyWhole(-n)}` : formatMoneyWhole(n));

/* An invoice's status in words (tone = the page's pill colour). */
export const statusMeta = (inv) => {
  switch (inv.status) {
    case 'paid': return { tone: 'success', label: inv.paid_on ? `Paid ${fmtDay(inv.paid_on, false)}` : 'Paid' };
    case 'past_due': return { tone: 'danger', label: `${inv.days_past_due} day${inv.days_past_due === 1 ? '' : 's'} past due` };
    case 'open': return { tone: 'blue', label: 'Open' };
    case 'credit': return { tone: 'neutral', label: 'Credit', title: 'A credit memo applied to this job' };
    // Gone from CE's open invoices with less collected than billed. Most were cleared with no payment
    // on their number at all (a credit, a void, or cash received on account and applied later).
    default: return {
      tone: 'warning', label: 'Closed',
      title: Math.abs(inv.collected) < 0.005
        ? 'Cleared in CE with no payment recorded against it (credited, voided or paid on account).'
        : `Cleared in CE with ${money(inv.collected)} of ${money(inv.amount)} collected (a short pay, credit or write-off).`,
    };
  }
};

/* One status for a project's invoices in the period: past due beats open beats the rest. */
export const projectStatus = (j) => {
  if (j.t.pastDueCount > 0) return { tone: 'danger', label: `${j.t.pastDueCount} past due` };
  if (j.t.openCount > 0) return { tone: 'blue', label: j.t.openCount > 1 ? `${j.t.openCount} open` : 'Open' };
  if (j.invoices.every((i) => i.status === 'credit')) return { tone: 'neutral', label: 'Credit' };
  if (j.invoices.some((i) => i.status === 'closed')) {
    return { tone: 'warning', label: 'Closed', title: 'One or more invoices cleared in CE with less collected than billed.' };
  }
  const paidOn = j.invoices.map((i) => i.paid_on).filter(Boolean).sort().pop();
  return { tone: 'success', label: paidOn ? `Paid ${fmtDay(paidOn, false)}` : 'Paid' };
};

const SORTS = {
  open: (a, b) => b.t.open - a.t.open,
  past_due: (a, b) => b.t.pastDue - a.t.pastDue || b.t.open - a.t.open,
  invoiced: (a, b) => b.t.invoiced - a.t.invoiced,
  latest: (a, b) => String(b.t.latest || '').localeCompare(String(a.t.latest || '')),
  name: (a, b) => a.name.localeCompare(b.name),
};

/* What the page shows for a choice of period and filters:
   - each job with only the invoices in the period, and its figures for them (t), its status
     bucket and its days to pay; jobs with nothing in the period drop out
   - the status counts (they follow the people / phase filters, as on Job Costs)
   - the jobs showing (status filter + sort), their totals, and days to pay over them: all of
     it, and each customer across their jobs. `job` narrows to one job (its export). */
export function selectView(jobs, { periodType = 'all', periodAt = null, status = 'all', phase = '', pm = '', cm = '', sales = '', sortBy = 'open', job = '' } = {}) {
  const inPeriod = (job ? jobs.filter((j) => j.job === job) : jobs).map((j) => {
    const invoices = periodType === 'all' ? j.invoices : j.invoices.filter((i) => periodKey(dateOf(i), periodType) === periodAt);
    const t = { invoiced: 0, collected: 0, open: 0, pastDue: 0, count: 0, openCount: 0, pastDueCount: 0, latest: null };
    invoices.forEach((i) => {
      t.invoiced += i.amount;
      t.collected += i.collected;
      t.open += i.open;
      if (i.amount > 0) t.count += 1;
      if (i.open > 0.004) t.openCount += 1;
      if (i.status === 'past_due') { t.pastDue += i.open; t.pastDueCount += 1; }
      const d = dateOf(i);
      if (d && (!t.latest || d > t.latest)) t.latest = d;
    });
    const bucket = t.pastDueCount > 0 ? 'past_due' : t.openCount > 0 ? 'open' : 'paid';
    const pay = invoices.map(daysToPay).filter((n) => n != null);
    return { ...j, invoices, t, bucket, pay };
  }).filter((j) => j.invoices.length > 0);

  const filtered = inPeriod.filter((j) => (
    (!phase || j.phases.includes(phase))
    && (!pm || j.pm.includes(pm))
    && (!cm || j.cm.includes(cm))
    && (!sales || j.sales.includes(sales))
  ));
  const counts = {
    all: filtered.length,
    open: filtered.filter((j) => j.bucket !== 'paid').length,
    past_due: filtered.filter((j) => j.bucket === 'past_due').length,
    paid: filtered.filter((j) => j.bucket === 'paid').length,
  };
  const by = SORTS[sortBy] || SORTS.open;
  const visible = filtered
    .filter((j) => (status === 'all' ? true : status === 'open' ? j.bucket !== 'paid' : j.bucket === status))
    .sort((a, b) => by(a, b) || a.name.localeCompare(b.name));

  const totals = { invoiced: 0, collected: 0, open: 0, pastDue: 0, count: 0, openCount: 0, pastDueCount: 0 };
  visible.forEach((j) => { Object.keys(totals).forEach((k) => { totals[k] += j.t[k]; }); });

  const byCustomer = new Map();
  visible.forEach((j) => {
    if (j.pay.length === 0) return;
    const c = byCustomer.get(customerOfJob(j)) || { days: [], jobs: 0 };
    c.days.push(...j.pay);
    c.jobs += 1;
    byCustomer.set(customerOfJob(j), c);
  });
  const customers = new Map([...byCustomer].map(([code, c]) => [code, { ...payStatsOf(c.days), jobs: c.jobs }]));
  const pay = { all: payStatsOf(visible.flatMap((j) => j.pay)), customers };

  return { counts, visible, totals, pay };
}

const STATUS_WORDS = { all: 'All', open: 'Open', past_due: 'Past due', paid: 'Paid' };

/* What the export covers, in words: "September 2026" or "Whole project", then the filters. */
export function describeView(view, data) {
  const job = view.job ? (data.jobs || []).find((j) => j.job === view.job) : null;
  const period = view.periodType === 'all' ? 'Whole project' : periodLabel(view.periodAt, view.periodType);
  const dept = view.phase ? ((data.departments || []).find((d) => d.phase === view.phase) || {}).label || `Phase ${view.phase}` : null;
  const filters = [
    dept,
    view.pm && `PM ${view.pm}`,
    view.cm && `CM ${view.cm}`,
    view.sales && `Sales ${view.sales}`,
    view.status && view.status !== 'all' && STATUS_WORDS[view.status],
  ].filter(Boolean);
  return {
    job,
    title: job ? job.name : (view.periodType === 'all' ? 'All invoices to date' : `${period} invoicing`),
    period,
    filters: filters.length ? filters.join(' · ') : 'All phases, PMs, CMs and sales people',
  };
}

/* The page's choices as URL parameters, for its exports (and back). */
export const viewToParams = (v) => {
  const p = new URLSearchParams();
  p.set('period', v.periodType || 'all');
  if (v.periodType !== 'all' && v.periodAt) p.set('at', v.periodAt);
  ['status', 'phase', 'pm', 'cm', 'sales', 'sortBy', 'job'].forEach((k) => { if (v[k] && v[k] !== 'all') p.set(k, v[k]); });
  return p;
};
export const viewFromParams = (p) => ({
  periodType: ['month', 'quarter', 'year', 'all'].includes(p.get('period')) ? p.get('period') : 'all',
  periodAt: p.get('at') || null,
  status: p.get('status') || 'all',
  phase: p.get('phase') || '',
  pm: p.get('pm') || '',
  cm: p.get('cm') || '',
  sales: p.get('sales') || '',
  sortBy: p.get('sortBy') || 'open',
  job: p.get('job') || '',
});
